<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { debounce } from '@chatwoot/utils';
import { buildThemeCss, googleFontHref } from '../lib/portalTheme';

const props = defineProps({
  templateId: { type: String, default: '' },
  document: { type: String, default: '' },
  theme: { type: Object, default: () => ({}) },
  selectedEventId: { type: [String, Number], default: null },
  deviceMode: { type: String, default: 'desktop' },
  annotate: { type: Boolean, default: false },
  interactive: { type: Boolean, default: true },
  portalEditableAttributeKeys: { type: Array, default: () => [] },
  /** Size iframe to full document height (no internal scroll). Used by the pannable canvas. */
  fitContent: { type: Boolean, default: false },
  /** Skip full node/tab geometry walks; still reports contentHeight. */
  geometryPaused: { type: Boolean, default: false },
  /** Website inbox id — included in live shell so the widget script loads. */
  inboxId: { type: [String, Number], default: '' },
});

const emit = defineEmits([
  'geometry',
  'loading',
  'editCommit',
  'editCancel',
  'contentHeight',
]);

const store = useStore();
const srcdoc = ref('');
const loading = ref(false);
const iframeRef = ref(null);
const frameReady = ref(false);
const softUpdating = ref(false);
const contentHeight = ref(560);

/** Keys that require a full iframe remount (shell / scripts / annotate mode). */
let hardKey = '';
/** Skip soft/hard refetch when document+theme+keys are unchanged (e.g. theme object echo). */
let softFingerprint = '';
/** Last document+keys fingerprint — theme-only changes skip Liquid. */
let contentFingerprint = '';
/** Abort in-flight preview when a newer one starts for this frame. */
let previewAbort = null;

const beginPreviewRequest = () => {
  if (previewAbort) previewAbort.abort();
  previewAbort = new AbortController();
  return previewAbort.signal;
};

const frameWidthClass = computed(() =>
  props.deviceMode === 'mobile' ? 'w-[390px] max-w-full' : 'w-full'
);

const computeContentFingerprint = () =>
  [
    props.document,
    JSON.stringify(props.portalEditableAttributeKeys || []),
  ].join('\0');

const computeSoftFingerprint = () =>
  [computeContentFingerprint(), JSON.stringify(props.theme || {})].join('\0');

const postToFrame = payload => {
  const win = iframeRef.value?.contentWindow;
  if (!win) return false;
  win.postMessage(payload, '*');
  return true;
};

const requestGeometry = () => {
  postToFrame({ type: 'portal-request-geometry' });
};

const activateTab = tabId => {
  postToFrame({ type: 'portal-activate-tab', tabId });
};

const patchSpans = (columnsId, spans, { skipGeometry = false } = {}) => {
  postToFrame({
    type: 'portal-patch-spans',
    columnsId,
    spans,
    skipGeometry: !!skipGeometry,
  });
};

const scrollBy = (deltaX, deltaY) => {
  postToFrame({ type: 'portal-scroll-by', deltaX, deltaY });
};

const startEdit = id => {
  postToFrame({ type: 'portal-start-edit', id });
};

const setGeometryPaused = paused => {
  postToFrame({ type: 'portal-set-geometry-paused', paused: !!paused });
};

defineExpose({
  requestGeometry,
  activateTab,
  patchSpans,
  scrollBy,
  startEdit,
  setGeometryPaused,
  iframeRef,
  contentHeight,
});

const computeHardKey = () =>
  [
    props.templateId,
    props.selectedEventId,
    props.annotate ? '1' : '0',
    props.inboxId || '',
  ].join('|');

const softReplace = (bodyHtml, themeCss, fontHref) => {
  const ok = postToFrame({
    type: 'portal-replace-body',
    html: bodyHtml,
    themeCss,
    fontHref: fontHref || null,
  });
  return ok;
};

const applyThemeOnly = () => {
  const themeCss = buildThemeCss(props.theme);
  const fontHref = googleFontHref(props.theme);
  return postToFrame({
    type: 'portal-update-theme',
    themeCss,
    fontHref: fontHref || null,
  });
};

const hardLoad = async nextHardKey => {
  loading.value = true;
  emit('loading', true);
  frameReady.value = false;
  const signal = beginPreviewRequest();
  try {
    const data = await store.dispatch('planningEventPortals/preview', {
      templateId: props.templateId,
      eventId: props.selectedEventId,
      document: props.document,
      theme: props.theme,
      annotate: props.annotate,
      shell: true,
      portalEditableAttributeKeys: props.portalEditableAttributeKeys,
      inboxId: props.inboxId || null,
      signal,
    });
    hardKey = nextHardKey || computeHardKey();
    srcdoc.value = data?.html ?? '';
    softFingerprint = computeSoftFingerprint();
    contentFingerprint = computeContentFingerprint();
  } catch (error) {
    if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
      return;
    }
    frameReady.value = false;
    hardKey = '';
    softFingerprint = '';
    contentFingerprint = '';
    srcdoc.value = `<!DOCTYPE html><html><body><p style="padding:24px;color:#888;">Preview unavailable</p></body></html>`;
    emit('geometry', { nodes: [], tabButtons: [] });
  } finally {
    loading.value = false;
    emit('loading', false);
  }
};

const fetchPreview = async ({ forceHard = false } = {}) => {
  if (!props.templateId || !props.selectedEventId) {
    srcdoc.value = '';
    frameReady.value = false;
    hardKey = '';
    contentFingerprint = '';
    emit('geometry', { nodes: [], tabButtons: [] });
    return;
  }

  const nextHardKey = computeHardKey();
  const nextSoftFingerprint = computeSoftFingerprint();
  const nextContentFingerprint = computeContentFingerprint();
  const canSoft =
    !forceHard &&
    frameReady.value &&
    hardKey === nextHardKey &&
    Boolean(srcdoc.value);

  // Same document/theme/keys — avoid "Updating…" flash from object-identity churn
  if (canSoft && nextSoftFingerprint === softFingerprint) {
    return;
  }

  // Theme-only change — patch CSS vars in the iframe, skip Liquid re-render
  if (
    canSoft &&
    nextContentFingerprint === contentFingerprint &&
    nextSoftFingerprint !== softFingerprint
  ) {
    if (applyThemeOnly()) {
      softFingerprint = nextSoftFingerprint;
      return;
    }
  }

  if (canSoft) {
    softUpdating.value = true;
    emit('loading', true);
    const signal = beginPreviewRequest();
    try {
      const data = await store.dispatch('planningEventPortals/preview', {
        templateId: props.templateId,
        eventId: props.selectedEventId,
        document: props.document,
        theme: props.theme,
        annotate: props.annotate,
        shell: false,
        portalEditableAttributeKeys: props.portalEditableAttributeKeys,
        inboxId: props.inboxId || null,
        signal,
      });
      const bodyHtml = data?.html ?? '';
      const replaced = softReplace(bodyHtml, data?.theme_css, data?.font_href);
      if (!replaced) {
        // Frame not ready — fall through to hard load
        await hardLoad(nextHardKey);
      } else {
        softFingerprint = nextSoftFingerprint;
        contentFingerprint = nextContentFingerprint;
      }
    } catch (error) {
      // Aborted by a newer request — keep last good frame
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
        return;
      }
      // Keep last good frame; don't wipe on soft-update failure
    } finally {
      softUpdating.value = false;
      emit('loading', false);
    }
    return;
  }

  await hardLoad(nextHardKey);
};

const debouncedFetch = debounce(() => fetchPreview(), 280);

watch(
  () => [
    props.templateId,
    props.selectedEventId,
    props.document,
    JSON.stringify(props.theme || {}),
    JSON.stringify(props.portalEditableAttributeKeys || []),
    props.annotate,
    props.inboxId,
  ],
  (next, prev) => {
    if (!props.selectedEventId) {
      fetchPreview({ forceHard: true });
      return;
    }
    // First run or hard-key change → immediate hard load; otherwise soft debounce
    const nextHard = computeHardKey();
    const prevHard = prev
      ? [prev[0], prev[1], prev[5] ? '1' : '0', prev[6] || ''].join('|')
      : null;

    // Theme-only while frame is ready → apply immediately (no debounce / Liquid)
    if (
      prev &&
      nextHard === prevHard &&
      frameReady.value &&
      next[2] === prev[2] &&
      next[4] === prev[4] &&
      next[3] !== prev[3]
    ) {
      if (applyThemeOnly()) {
        softFingerprint = computeSoftFingerprint();
        return;
      }
    }

    if (!prev || nextHard !== prevHard || !frameReady.value) {
      fetchPreview({ forceHard: true });
    } else {
      debouncedFetch();
    }
  },
  { immediate: true }
);

const onMessage = event => {
  const data = event.data;
  if (!data || typeof data !== 'object') return;
  if (
    iframeRef.value?.contentWindow &&
    event.source !== iframeRef.value.contentWindow
  ) {
    return;
  }
  if (data.type === 'portal-geometry') {
    frameReady.value = true;
    if (typeof data.contentHeight === 'number' && data.contentHeight > 0) {
      contentHeight.value = Math.ceil(data.contentHeight);
      emit('contentHeight', contentHeight.value);
    }
    emit('geometry', {
      nodes: data.nodes || [],
      tabButtons: data.tabButtons || [],
      contentHeight: contentHeight.value,
    });
    return;
  }
  if (data.type === 'portal-body-ready') {
    frameReady.value = true;
    return;
  }
  if (data.type === 'portal-edit-commit') {
    emit('editCommit', { id: data.id, content: data.text || '' });
    return;
  }
  if (data.type === 'portal-edit-cancel') {
    emit('editCancel', { id: data.id });
  }
};

const onIframeLoad = () => {
  frameReady.value = true;
  setGeometryPaused(props.geometryPaused);
  if (!props.geometryPaused) requestGeometry();
};

watch(
  () => props.geometryPaused,
  paused => {
    setGeometryPaused(paused);
  }
);

onMounted(() => {
  window.addEventListener('message', onMessage);
});

onBeforeUnmount(() => {
  if (previewAbort) {
    previewAbort.abort();
    previewAbort = null;
  }
  window.removeEventListener('message', onMessage);
});
</script>

<template>
  <div class="relative w-full h-full min-h-0 flex flex-col">
    <div
      v-if="loading && !srcdoc"
      class="absolute inset-0 flex items-center justify-center bg-n-slate-1/80 z-10"
    >
      <span class="i-lucide-loader-2 size-5 animate-spin text-n-slate-9" />
    </div>
    <div
      v-else-if="softUpdating"
      class="absolute top-2 right-2 z-10 px-2 py-1 rounded bg-white/90 text-[10px] text-n-slate-9 border border-n-slate-5 pointer-events-none"
    >
      <span
        class="i-lucide-loader-2 size-3 animate-spin inline-block align-middle mr-1"
      />
      Updating…
    </div>
    <div
      class="flex-1 min-h-0 flex"
      :class="[
        fitContent ? 'overflow-hidden' : 'overflow-auto justify-center',
        deviceMode === 'mobile' ? 'bg-n-slate-3 p-4' : '',
      ]"
    >
      <div
        class="overflow-hidden bg-white"
        :class="[
          fitContent
            ? 'w-full h-full'
            : ['h-full min-h-[50vh]', frameWidthClass],
          deviceMode === 'mobile'
            ? 'rounded-xl border border-n-slate-5 shadow-sm'
            : fitContent
              ? ''
              : 'border-0 shadow-none',
        ]"
      >
        <iframe
          v-if="srcdoc"
          ref="iframeRef"
          :srcdoc="srcdoc"
          sandbox="allow-scripts"
          class="w-full h-full border-0 bg-white"
          :class="interactive ? '' : 'pointer-events-none'"
          @load="onIframeLoad"
        />
      </div>
    </div>
  </div>
</template>
